import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const success = await login(email, password);
      if (success) {
        // Só limpa os campos se o login foi bem-sucedido
        setEmail('');
        setPassword('');
        navigate('/dashboard');
      } else {
        // O AuthContext já mostra o toast com o erro
        // Não mostra mais erro no formulário - apenas no toast
        // NÃO limpa os campos em caso de erro para melhor UX
      }
    } catch (err: any) {
      // Fallback caso algo dê errado - apenas no toast
      // NÃO limpa os campos em caso de erro
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-white px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <div className="flex items-center justify-center mb-12">
              <div className="flex items-center space-x-8">
                <img
                  src="/logo-wabbycall.svg"
                  alt="WabbyCall"
                  className="h-20 w-auto"
                />
                <span className="text-5xl font-bold" style={{fontFamily: 'Orbitron, sans-serif', color: '#31b4e7'}}>WabbyCall</span>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-8">
              Acesse sua conta
            </h2>
            <p className="text-sm text-gray-600 mb-8">
              Faça login para continuar acessando nossos serviços
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                E-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                placeholder="Digite seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Lembrar-me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-cyan-600 hover:text-cyan-500"
                >
                  Esqueceu a senha?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-500 hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'ENTRAR'
                )}
              </button>
            </div>

            <div className="text-center text-sm text-gray-600">
              Não tem uma conta?{' '}
              <Link
                to="/register"
                className="font-medium text-cyan-600 hover:text-cyan-500"
              >
                Criar conta
              </Link>
            </div>

            <div className="text-center text-sm text-gray-600">
              Ao continuar, você concorda com os termos de serviço
            </div>
          </form>
        </div>
      </div>

      {/* Right side - Welcome Message with Gradient Background */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-cyan-500 to-blue-600">
          {/* Decorative elements */}
          <div className="absolute top-8 right-8">
            <div className="grid grid-cols-6 gap-2">
              {[...Array(18)].map((_, i) => (
                <div key={i} className="w-2 h-2 bg-white bg-opacity-30 rounded-full"></div>
              ))}
            </div>
          </div>
          
          <div className="absolute top-1/4 left-8">
            <div className="w-16 h-16 border-4 border-white border-opacity-30 rounded-full"></div>
          </div>
          
          <div className="absolute bottom-1/4 right-16">
            <div className="w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>
          </div>

          {/* Curved decorative lines */}
          <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 400 600" fill="none">
            <path
              d="M-50 200C100 150 200 250 350 200"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M-50 350C100 300 200 400 350 350"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="2"
              fill="none"
            />
          </svg>

          {/* Main content */}
          <div className="relative z-10 h-full flex items-center justify-center px-8">
            <div className="text-center text-white">
              <div className="mb-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 bg-cyan-500 rounded-full"></div>
                  </div>
                </div>
              </div>
              <h1 className="text-4xl font-bold mb-4">
                Seja bem vindo<br />
                ao WabbyCall
              </h1>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;